/**
 * Post Type Service
 * Handles all post-type related operations including validation, auto-detection, and file handling
 * 
 * @version 1.0.0
 * @author JGS Tech Team
 */

import api from '../helpers/axios';
import {
  PostType,
  FileType,
  PostTypeConfig,
  EnhancedFileUploadRequest,
  EnhancedFileUploadResponse,
  PostTypeValidationResult,
  PostTypeDetectionCriteria,
  PostTypeServiceConfig,
  PostTypeError,
  FileValidationError,
  EnhancedArticlePayload,
  EnhancedArticleResponse
} from '../types/postTypes';

/**
 * Default post type configurations
 */
const DEFAULT_POST_TYPE_CONFIGS: Record<PostType, PostTypeConfig> = {
  [PostType.NORMAL_POST]: {
    typeName: PostType.NORMAL_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    displayName: 'Normal Post',
    description: 'Standard text articles with image support',
    icon: 'Article',
    validationRules: {
      maxSubArticles: 50,
      requiredFields: ['title', 'description', 'body']
    }
  },
  [PostType.IMAGE_POST]: {
    typeName: PostType.IMAGE_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    displayName: 'Image Post',
    description: 'Image-focused content with enhanced visual features',
    icon: 'Image',
    validationRules: {
      maxSubArticles: 20,
      requiredFields: ['title', 'description', 'fileUrl']
    }
  },
  [PostType.VIDEO_POST]: {
    typeName: PostType.VIDEO_POST,
    allowedExtensions: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv', 'jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: false,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.VIDEO,
    maxFileSize: 100 * 1024 * 1024, // 100MB
    displayName: 'Video Post',
    description: 'Video content with thumbnail support',
    icon: 'VideoLibrary',
    validationRules: {
      maxSubArticles: 5,
      requiredFields: ['title', 'description', 'fileUrl', 'thumbnailPath']
    }
  },
  [PostType.MAGAZINE_POST]: {
    typeName: PostType.MAGAZINE_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    displayName: 'Magazine Post',
    description: 'Long-form articles with document-style layout',
    icon: 'MenuBook',
    validationRules: {
      minSubArticles: 2,
      maxSubArticles: 100,
      requiredFields: ['title', 'description', 'body']
    }
  },
  [PostType.CAROUSEL_POST]: {
    typeName: PostType.CAROUSEL_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB per image
    displayName: 'Carousel Post',
    description: 'Multiple image galleries with slideshow functionality',
    icon: 'ViewCarousel',
    validationRules: {
      minSubArticles: 2,
      maxSubArticles: 10,
      requiredFields: ['title', 'description']
    }
  },
  [PostType.OPINION_POLL]: {
    typeName: PostType.OPINION_POLL,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif'],
    supportsMultipleSubArticles: false,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    displayName: 'Opinion Poll',
    description: 'Interactive polls with multiple choice options',
    icon: 'Poll',
    validationRules: {
      maxSubArticles: 0,
      requiredFields: ['title', 'description', 'pollData']
    }
  },
  [PostType.ADVERTISEMENT_POST]: {
    typeName: PostType.ADVERTISEMENT_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'mp4'],
    supportsMultipleSubArticles: false,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 100 * 1024 * 1024, // 100MB for video ads
    displayName: 'Advertisement',
    description: 'Promotional content with targeting options (DEPRECATED: Use any post type with "Sponsored Content" option instead)',
    icon: 'Ads',
    hidden: true, // Hidden from dropdown - any post type can now be an advertisement
    deprecated: true, // Marked as deprecated - kept for backwards compatibility with existing data
    validationRules: {
      maxSubArticles: 0,
      requiredFields: ['title', 'description', 'adData']
    }
  },
  [PostType.PRODUCT_POST]: {
    typeName: PostType.PRODUCT_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB
    displayName: 'Product Post',
    description: 'E-commerce product listings with detailed specifications',
    icon: 'ShoppingCart',
    validationRules: {
      maxSubArticles: 10,
      requiredFields: ['title', 'description', 'productData']
    }
  },
  [PostType.FULL_TEXT_POST]: {
    typeName: PostType.FULL_TEXT_POST,
    allowedExtensions: [], // Text-only, no file uploads
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.DOCUMENT,
    maxFileSize: 0, // No files allowed
    displayName: 'Full Text Post',
    description: 'Text-only articles without media content',
    icon: 'Article',
    validationRules: {
      maxSubArticles: 100,
      requiredFields: ['title', 'description', 'body']
    }
  },
  [PostType.SHORT_VIDEO_POST]: {
    typeName: PostType.SHORT_VIDEO_POST,
    allowedExtensions: ['mp4', 'webm', 'mov', 'jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: false,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.VIDEO,
    maxFileSize: 50 * 1024 * 1024, // 50MB for short videos
    displayName: 'Short Video Post',
    description: 'Portrait orientation videos optimized for mobile viewing',
    icon: 'Videocam',
    validationRules: {
      maxSubArticles: 2,
      requiredFields: ['title', 'description', 'fileUrl', 'thumbnailPath']
    }
  },
  [PostType.PODCAST_POST]: {
    typeName: PostType.PODCAST_POST,
    allowedExtensions: ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma', 'jpg', 'jpeg', 'png', 'gif', 'webp'],
    supportsMultipleSubArticles: false,
    requiresSpecializedStorage: true,
    primaryFileType: FileType.AUDIO,
    maxFileSize: 200 * 1024 * 1024, // 200MB for audio files
    displayName: 'Podcast Post',
    description: 'Audio content with cover art for podcast episodes',
    icon: 'Headphones',
    validationRules: {
      maxSubArticles: 5,
      requiredFields: ['title', 'description', 'fileUrl', 'thumbnailPath']
    }
  },
  [PostType.CONTENT_CAROUSEL_POST]: {
    typeName: PostType.CONTENT_CAROUSEL_POST,
    allowedExtensions: ['jpg', 'jpeg', 'png', 'webp'],
    supportsMultipleSubArticles: true,
    requiresSpecializedStorage: false,
    primaryFileType: FileType.IMAGE,
    maxFileSize: 10 * 1024 * 1024, // 10MB — single 9:16 background image
    displayName: 'Content Carousel',
    description: 'Fixed 9:16 portrait background image; sub-articles transition title and body only via horizontal swipe — the image never changes',
    icon: 'ViewDay',
    validationRules: {
      minSubArticles: 1,
      maxSubArticles: 20,
      requiredFields: ['title', 'description', 'fileUrl']
    }
  }
};

/**
 * Auto-detection criteria for different post types
 */
const POST_TYPE_DETECTION_CRITERIA: Record<PostType, PostTypeDetectionCriteria> = {
  [PostType.VIDEO_POST]: {
    fileExtensions: ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'],
    contentPatterns: [/video/i, /watch/i, /stream/i],
    subArticleThresholds: { max: 5 },
    keywordMatches: ['video', 'watch', 'streaming', 'movie'],
    priority: 10
  },
  [PostType.CAROUSEL_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    contentPatterns: [/gallery/i, /carousel/i, /slideshow/i],
    subArticleThresholds: { min: 4 },
    keywordMatches: ['gallery', 'carousel', 'slideshow', 'images'],
    priority: 8
  },
  [PostType.MAGAZINE_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'webp'],
    contentPatterns: [/article/i, /magazine/i, /journal/i],
    subArticleThresholds: { min: 3 },
    keywordMatches: ['magazine', 'article', 'journal', 'feature'],
    priority: 6
  },
  [PostType.OPINION_POLL]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif'],
    contentPatterns: [/poll/i, /vote/i, /survey/i, /opinion/i],
    subArticleThresholds: { max: 0 },
    keywordMatches: ['poll', 'vote', 'survey', 'opinion', 'choose'],
    priority: 9
  },
  [PostType.ADVERTISEMENT_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'mp4'],
    contentPatterns: [/ad/i, /advertisement/i, /promo/i, /offer/i],
    subArticleThresholds: { max: 0 },
    keywordMatches: ['advertisement', 'promo', 'offer', 'deal', 'sale'],
    priority: 7
  },
  [PostType.PRODUCT_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    contentPatterns: [/product/i, /shop/i, /buy/i, /price/i],
    subArticleThresholds: { max: 10 },
    keywordMatches: ['product', 'shop', 'buy', 'price', 'store'],
    priority: 7
  },
  [PostType.IMAGE_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    contentPatterns: [/photo/i, /image/i, /picture/i],
    subArticleThresholds: { max: 20 },
    keywordMatches: ['photo', 'image', 'picture', 'visual'],
    priority: 4
  },
  [PostType.FULL_TEXT_POST]: {
    fileExtensions: [], // Text-only
    contentPatterns: [/text/i, /article/i, /story/i, /news/i],
    subArticleThresholds: { min: 1 },
    keywordMatches: ['text', 'article', 'story', 'news', 'blog'],
    priority: 5
  },
  [PostType.SHORT_VIDEO_POST]: {
    fileExtensions: ['mp4', 'webm', 'mov'],
    contentPatterns: [/short/i, /reel/i, /story/i, /vertical/i],
    subArticleThresholds: { max: 2 },
    keywordMatches: ['short', 'reel', 'story', 'vertical', 'mobile'],
    priority: 9
  },
  [PostType.PODCAST_POST]: {
    fileExtensions: ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma'],
    contentPatterns: [/podcast/i, /audio/i, /episode/i, /listen/i, /radio/i],
    subArticleThresholds: { max: 5 },
    keywordMatches: ['podcast', 'audio', 'episode', 'listen', 'radio', 'show', 'interview'],
    priority: 10
  },
  [PostType.NORMAL_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'],
    contentPatterns: [/.*/],
    subArticleThresholds: {},
    keywordMatches: [],
    priority: 1 // Lowest priority - default fallback
  },
  [PostType.CONTENT_CAROUSEL_POST]: {
    fileExtensions: ['jpg', 'jpeg', 'png', 'webp'],
    contentPatterns: [/content.carousel/i, /fixed.background/i, /overlay/i],
    subArticleThresholds: { min: 1, max: 20 },
    keywordMatches: ['content carousel', 'overlay', 'fixed image', 'text carousel'],
    priority: 8
  }
};

/**
 * PostTypeService class for managing post type operations
 */
export class PostTypeService {
  private config: PostTypeServiceConfig;
  private postTypeConfigs: Record<PostType, PostTypeConfig>;

  constructor(config?: Partial<PostTypeServiceConfig>) {
    this.config = {
      enableAutoDetection: true,
      defaultPostType: PostType.NORMAL_POST,
      fileUploadBaseUrl: '/api/articles',
      maxFileSizes: Object.fromEntries(
        Object.entries(DEFAULT_POST_TYPE_CONFIGS).map(([key, value]) => [
          key,
          value.maxFileSize || 10 * 1024 * 1024
        ])
      ) as Record<PostType, number>,
      allowedExtensions: Object.fromEntries(
        Object.entries(DEFAULT_POST_TYPE_CONFIGS).map(([key, value]) => [
          key,
          value.allowedExtensions
        ])
      ) as Record<PostType, string[]>,
      storagePaths: {
        [PostType.NORMAL_POST]: '/uploads/posts',
        [PostType.IMAGE_POST]: '/uploads/images',
        [PostType.VIDEO_POST]: '/uploads/videos',
        [PostType.MAGAZINE_POST]: '/uploads/magazines',
        [PostType.CAROUSEL_POST]: '/uploads/carousels',
        [PostType.OPINION_POLL]: '/uploads/polls',
        [PostType.ADVERTISEMENT_POST]: '/uploads/advertisements',
        [PostType.PRODUCT_POST]: '/uploads/products',
        [PostType.FULL_TEXT_POST]: '/uploads/texts',
        [PostType.SHORT_VIDEO_POST]: '/uploads/short-videos',
        [PostType.PODCAST_POST]: '/uploads/podcasts',
        [PostType.CONTENT_CAROUSEL_POST]: '/uploads/content-carousels'
      },
      validation: {
        strictMode: false,
        validateFileTypes: true,
        validateSubArticleLimits: true
      },
      ...config
    };

    this.postTypeConfigs = { ...DEFAULT_POST_TYPE_CONFIGS };
  }

  /**
   * Get all available post types and their configurations
   */
  async getPostTypes(): Promise<Record<PostType, PostTypeConfig>> {
    try {
      // Try to fetch from backend first
      const response = await api.get('/api/articles/post-types');
      const apiData = response.data;
      
      // Transform API data to include display information
      const transformedData: Partial<Record<PostType, PostTypeConfig>> = {};
      
      for (const [key, value] of Object.entries(apiData)) {
        const postType = key as PostType;
        const apiConfig = value as any;
        
        // Get default config for display information
        const defaultConfig = DEFAULT_POST_TYPE_CONFIGS[postType];
        
        transformedData[postType] = {
          typeName: postType,
          allowedExtensions: apiConfig.allowedExtensions || defaultConfig?.allowedExtensions || [],
          supportsMultipleSubArticles: apiConfig.supportsMultipleSubArticles ?? defaultConfig?.supportsMultipleSubArticles ?? false,
          requiresSpecializedStorage: apiConfig.requiresSpecializedStorage ?? defaultConfig?.requiresSpecializedStorage ?? false,
          primaryFileType: apiConfig.primaryFileType || defaultConfig?.primaryFileType || FileType.IMAGE,
          maxFileSize: apiConfig.maxFileSize || defaultConfig?.maxFileSize || 10 * 1024 * 1024,
          displayName: defaultConfig?.displayName || this.formatPostTypeName(postType),
          description: defaultConfig?.description || `${this.formatPostTypeName(postType)} content`,
          icon: defaultConfig?.icon || 'Article',
          validationRules: apiConfig.validationRules || defaultConfig?.validationRules
        };
      }
      
      // Update the instance cache
      this.postTypeConfigs = { ...this.postTypeConfigs, ...transformedData };
      
      return this.postTypeConfigs;
    } catch (error) {
      console.warn('Failed to fetch post types from backend, using defaults:', error);
      return this.postTypeConfigs;
    }
  }

  /**
   * Format post type name for display
   */
  private formatPostTypeName(postType: PostType): string {
    return postType
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase());
  }

  /**
   * Get configuration for a specific post type
   */
  getPostTypeConfig(postType: PostType): PostTypeConfig {
    // First check if we have cached data from API
    const config = this.postTypeConfigs[postType];
    if (config) {
      return config;
    }
    
    // Fallback to default config
    const defaultConfig = DEFAULT_POST_TYPE_CONFIGS[postType];
    if (!defaultConfig) {
      throw new PostTypeError(`Invalid post type: ${postType}`, postType);
    }
    
    return defaultConfig;
  }

  /**
   * Validate file for a specific post type
   */
  validateFile(file: File, postType: PostType, fileCategory: string = 'file'): PostTypeValidationResult {
    const config = this.getPostTypeConfig(postType);
    const errors: string[] = [];
    const warnings: string[] = [];

    // Extract file extension
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension) {
      errors.push('File must have a valid extension');
    } else {
      // Special handling for media posts - differentiate between main content and thumbnail
      let allowedExtensions = config.allowedExtensions;
      
      // For thumbnails, always allow image formats regardless of post type
      if (fileCategory === 'thumbnail') {
        allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'];
      } else if (postType === PostType.VIDEO_POST || postType === PostType.SHORT_VIDEO_POST) {
        if (fileCategory === 'file' || fileCategory === 'video') {
          // For main video content, allow video formats
          if (postType === PostType.VIDEO_POST) {
            allowedExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
          } else if (postType === PostType.SHORT_VIDEO_POST) {
            allowedExtensions = ['mp4', 'webm', 'mov']; // Short videos have more restrictive formats
          }
        }
      } else if (postType === PostType.PODCAST_POST) {
        if (fileCategory === 'file' || fileCategory === 'audio') {
          // For main audio content, allow audio formats only
          allowedExtensions = ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'flac', 'wma'];
        }
      }
      
      if (!allowedExtensions.includes(fileExtension)) {
        const categoryText = fileCategory === 'thumbnail' ? 'thumbnail' : 'main content';
        errors.push(
          `File extension '.${fileExtension}' is not allowed for ${config.displayName} ${categoryText}. ` +
          `Allowed extensions: ${allowedExtensions.join(', ')}`
        );
      }
    }

    // Check file size - for thumbnails, use a fixed 5MB limit
    const maxSize = fileCategory === 'thumbnail' ? 5 * 1024 * 1024 : config.maxFileSize!;
    if (file.size > maxSize) {
      errors.push(
        `File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum allowed size ` +
        `(${(maxSize / 1024 / 1024).toFixed(2)}MB) for ${config.displayName} ${fileCategory}`
      );
    }

    // Additional validation based on file type - skip for thumbnails (thumbnails are always images)
    if (fileCategory !== 'thumbnail') {
      if (config.primaryFileType === FileType.VIDEO) {
        if (!file.type.startsWith('video/')) {
          errors.push(`${config.displayName} requires a video file`);
        }
      } else if (config.primaryFileType === FileType.IMAGE) {
        if (!file.type.startsWith('image/')) {
          errors.push(`${config.displayName} requires an image file`);
        }
      } else if (config.primaryFileType === FileType.AUDIO) {
        if (!file.type.startsWith('audio/')) {
          errors.push(`${config.displayName} requires an audio file`);
        }
      }
    } else {
      // For thumbnails, must be an image
      if (!file.type.startsWith('image/')) {
        errors.push('Thumbnail must be an image file');
      }
    }

    // Check for common issues
    if (file.size < 1024) {
      warnings.push('File size is very small, this might be an empty or corrupted file');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Auto-detect post type based on content and files
   */
  detectPostType(
    title: string,
    description: string,
    body: string,
    files: File[],
    subArticleCount: number
  ): PostType {
    if (!this.config.enableAutoDetection) {
      return this.config.defaultPostType;
    }

    const content = `${title} ${description} ${body}`.toLowerCase();
    const fileExtensions = files.map(f => f.name.split('.').pop()?.toLowerCase()).filter(Boolean);
    
    let bestMatch: { postType: PostType; score: number } = {
      postType: this.config.defaultPostType,
      score: 0
    };

    for (const [postType, criteria] of Object.entries(POST_TYPE_DETECTION_CRITERIA)) {
      let score = 0;

      // Check file extensions
      if (fileExtensions.some(ext => criteria.fileExtensions.includes(ext!))) {
        score += criteria.priority * 2;
      }

      // Check content patterns
      if (criteria.contentPatterns.some(pattern => pattern.test(content))) {
        score += criteria.priority;
      }

      // Check keyword matches
      const keywordMatches = criteria.keywordMatches.filter(keyword => 
        content.includes(keyword)
      ).length;
      score += keywordMatches * criteria.priority * 0.5;

      // Check sub-article thresholds
      if (criteria.subArticleThresholds.min && subArticleCount >= criteria.subArticleThresholds.min) {
        score += criteria.priority * 0.5;
      }
      if (criteria.subArticleThresholds.max && subArticleCount <= criteria.subArticleThresholds.max) {
        score += criteria.priority * 0.3;
      }

      if (score > bestMatch.score) {
        bestMatch = { postType: postType as PostType, score };
      }
    }

    return bestMatch.postType;
  }

  /**
   * Enhanced file upload with post-type specific handling
   */
  async uploadFile(request: EnhancedFileUploadRequest): Promise<EnhancedFileUploadResponse> {
    const { file, externalUrl, postType, fileCategory = 'file', metadata = {} } = request;

    // Validate that either file or externalUrl is provided, not both
    if ((!file && !externalUrl) || (file && externalUrl)) {
      throw new PostTypeError(
        'Either file or externalUrl must be provided, not both',
        postType,
        undefined,
        'VALIDATION_ERROR'
      );
    }

    // Validate file if provided
    if (file && postType) {
      const validation = this.validateFile(file, postType, fileCategory);
      if (!validation.isValid) {
        throw new FileValidationError(
          validation.errors.join('. '),
          file.name,
          file.type,
          this.getPostTypeConfig(postType).allowedExtensions
        );
      }
    }

    try {
      const formData = new FormData();
      
      if (file) {
        formData.append('file', file);
      }
      
      if (externalUrl) {
        formData.append('externalUrl', externalUrl);
      }
      
      if (postType) {
        formData.append('postType', postType);
      }
      
      formData.append('fileCategory', fileCategory);
      
      if (Object.keys(metadata).length > 0) {
        formData.append('metadata', JSON.stringify(metadata));
      }

      const response = await api.post('/api/articles/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true,
        timeout: 300000, // 5 minutes for large video uploads
      });

      return {
        filePath: response.data.filePath,
        postType: response.data.postType,
        fileSize: file?.size || 0,
        mimeType: file?.type || 'external/url',
        originalName: response.data.originalFileName || (file ? file.name : 'external_content'),
        uploadedAt: new Date().toISOString(),
        metadata: {
          ...metadata,
          contentSource: response.data.contentSource || (file ? 'DIRECT' : 'EXTERNAL'),
          ...response.data.metadata
        }
      };
    } catch (error: any) {
      if (error.response?.status === 413) {
        throw new FileValidationError(
          'File size exceeds server limits',
          file?.name || 'external_url',
          file?.type || 'external/url',
          []
        );
      }
      throw new PostTypeError(
        `Failed to upload content: ${error.message}`,
        postType,
        undefined,
        'UPLOAD_ERROR'
      );
    }
  }

  /**
   * Validate article payload for specific post type
   */
  validateArticlePayload(payload: EnhancedArticlePayload): PostTypeValidationResult {
    const postType = payload.postType || this.config.defaultPostType;
    const config = this.getPostTypeConfig(postType);
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate required fields
    if (config.validationRules?.requiredFields) {
      for (const field of config.validationRules.requiredFields) {
        if (field === 'pollData' && postType === PostType.OPINION_POLL) {
          if (!payload.pollData) {
            errors.push('Poll data is required for opinion poll posts');
          } else if (!payload.pollData.options || payload.pollData.options.length < 2) {
            errors.push('Poll must have at least 2 options');
          }
        } else if (field === 'adData' && postType === PostType.ADVERTISEMENT_POST) {
          if (!payload.adData) {
            errors.push('Advertisement data is required for advertisement posts');
          }
        } else if (field === 'productData' && postType === PostType.PRODUCT_POST) {
          if (!payload.productData) {
            errors.push('Product data is required for product posts');
          }
        } else if (field === 'fileUrl' && !payload.fileUrl) {
          errors.push(`${field} is required for ${config.displayName}`);
        } else if (!payload[field as keyof EnhancedArticlePayload]) {
          errors.push(`${field} is required for ${config.displayName}`);
        }
      }
    }

    // Validate sub-articles limits
    if (this.config.validation.validateSubArticleLimits && config.validationRules) {
      const subArticleCount = payload.subArticles?.length || 0;
      
      if (config.validationRules.minSubArticles && subArticleCount < config.validationRules.minSubArticles) {
        errors.push(
          `${config.displayName} requires at least ${config.validationRules.minSubArticles} sub-articles`
        );
      }
      
      if (config.validationRules.maxSubArticles && subArticleCount > config.validationRules.maxSubArticles) {
        errors.push(
          `${config.displayName} allows maximum ${config.validationRules.maxSubArticles} sub-articles`
        );
      }
    }

    // Custom validation if defined
    if (config.validationRules?.customValidation) {
      const customError = config.validationRules.customValidation(payload);
      if (customError) {
        errors.push(customError);
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      detectedPostType: postType
    };
  }

  /**
   * Create article with enhanced post-type support
   */
  async createArticle(payload: EnhancedArticlePayload): Promise<EnhancedArticleResponse> {
    // Auto-detect post type if not provided
    if (!payload.postType && this.config.enableAutoDetection) {
      payload.postType = this.detectPostType(
        payload.title,
        payload.description,
        payload.body,
        [], // Files would need to be passed separately
        payload.subArticles?.length || 0
      );
    }

    // Validate payload
    const validation = this.validateArticlePayload(payload);
    if (!validation.isValid) {
      throw new PostTypeError(
        'Article validation failed',
        payload.postType,
        validation.errors,
        'VALIDATION_ERROR'
      );
    }

    try {
      const response = await api.post('/api/articles/create', payload, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return {
        ...response.data,
        postType: response.data.postType || payload.postType || this.config.defaultPostType,
        postTypeConfig: this.getPostTypeConfig(response.data.postType || payload.postType || this.config.defaultPostType)
      };
    } catch (error: any) {
      throw new PostTypeError(
        `Failed to create article: ${error.message}`,
        payload.postType,
        undefined,
        'CREATE_ERROR'
      );
    }
  }

  /**
   * Update article with post-type validation
   */
  async updateArticle(id: string, payload: EnhancedArticlePayload): Promise<EnhancedArticleResponse> {
    // Validate payload
    const validation = this.validateArticlePayload(payload);
    if (!validation.isValid) {
      throw new PostTypeError(
        'Article validation failed',
        payload.postType,
        validation.errors,
        'VALIDATION_ERROR'
      );
    }

    try {
      const response = await api.put(`/api/articles/${id}`, payload);

      return {
        ...response.data,
        postType: response.data.postType || payload.postType || this.config.defaultPostType,
        postTypeConfig: this.getPostTypeConfig(response.data.postType || payload.postType || this.config.defaultPostType)
      };
    } catch (error: any) {
      throw new PostTypeError(
        `Failed to update article: ${error.message}`,
        payload.postType,
        undefined,
        'UPDATE_ERROR'
      );
    }
  }

  /**
   * Get article with enhanced post-type information
   */
  async getArticle(id: string): Promise<EnhancedArticleResponse> {
    try {
      const response = await api.get(`/api/articles/${id}`);
      const postType = response.data.postType || this.config.defaultPostType;

      return {
        ...response.data,
        postType,
        postTypeConfig: this.getPostTypeConfig(postType)
      };
    } catch (error: any) {
      throw new PostTypeError(
        `Failed to fetch article: ${error.message}`,
        undefined,
        undefined,
        'FETCH_ERROR'
      );
    }
  }

  /**
   * Get file extension validation rules for a post type
   */
  getFileExtensionRules(postType: PostType): { allowed: string[]; maxSize: number } {
    const config = this.getPostTypeConfig(postType);
    return {
      allowed: config.allowedExtensions,
      maxSize: config.maxFileSize || 10 * 1024 * 1024
    };
  }

  /**
   * Check if post type supports multiple sub-articles
   */
  supportsMultipleSubArticles(postType: PostType): boolean {
    return this.getPostTypeConfig(postType).supportsMultipleSubArticles;
  }

  /**
   * Get display information for UI components
   */
  getPostTypeDisplayInfo(postType: PostType): { name: string; description: string; icon?: string } {
    const config = this.getPostTypeConfig(postType);
    return {
      name: config.displayName,
      description: config.description,
      icon: config.icon
    };
  }
}

// Export singleton instance
export const postTypeService = new PostTypeService();
export default postTypeService;
